import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { JOB_TRANSITIONS, type Estimate, type Invoice, type Job, type JobStatus } from '@worksy360/shared';
import { api, ApiRequestError } from '../lib/api.js';
import { useCreateProperty, useEquipment, useMemberships, useProperties, useRefreshCustomer, useTimeline } from '../lib/hooks.js';
import { Badge, Button, Card, Dialog, Drawer, EmptyState, Field, Input, Skeleton, Textarea } from '../ui/index.js';
import { useToast } from '../ui/toast.js';
import { label, money, shortDate, statusTone } from '../lib/format.js';
import { NewEstimateDrawer } from '../components/NewEstimateDrawer.js';
import { MessageThread } from '../components/MessageThread.js';

export function CustomerDetail() {
  const { id = '' } = useParams();
  const { data, isLoading, error } = useTimeline(id);
  const refresh = useRefreshCustomer(id);
  const toast = useToast();
  const [estDrawer, setEstDrawer] = useState(false);
  const [payFor, setPayFor] = useState<Invoice | null>(null);
  const [approveEst, setApproveEst] = useState<Estimate | null>(null);
  const [propDrawer, setPropDrawer] = useState(false);
  const [invDrawer, setInvDrawer] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [equipDrawer, setEquipDrawer] = useState(false);
  const [memberDrawer, setMemberDrawer] = useState(false);
  const properties = useProperties(id);
  const equipment = useEquipment(id);
  const memberships = useMemberships(id);
  const qc = useQueryClient();

  const chargeCard = async (inv: Invoice) => {
    setBusy(`card${inv.id}`);
    try {
      const intent = await api.post<{ paymentId: string; mode: string; clientSecret: string }>(`/customers/${id}/invoices/${inv.id}/payment-intent`, {});
      if (intent.mode === 'fake') {
        // Dev/test provider: complete the payment through the real webhook path.
        await api.post(`/customers/${id}/payments/${intent.paymentId}/simulate?outcome=succeeded`);
        refresh();
        toast.push('Card payment completed (test provider)', 'success');
      } else {
        // Stripe mode: the browser would confirm intent.clientSecret with Stripe Elements here.
        toast.push('Stripe Elements card entry opens here once live keys are configured', 'default');
      }
    } catch (err) {
      toast.push(err instanceof ApiRequestError ? err.message : 'Charge failed', 'error');
    } finally {
      setBusy(null);
    }
  };

  const act = async (key: string, fn: () => Promise<unknown>, successMsg?: string) => {
    setBusy(key);
    try {
      await fn();
      refresh();
      if (successMsg) toast.push(successMsg, 'success');
    } catch (err) {
      toast.push(err instanceof ApiRequestError ? err.message : 'Something went wrong', 'error');
    } finally {
      setBusy(null);
    }
  };

  if (isLoading) return <Card className="card-pad"><Skeleton h={120} /></Card>;
  if (error || !data) return <Card className="card-pad"><EmptyState icon="⚠️" title="Customer not found" action={<Link to="/customers"><Button variant="secondary">Back to customers</Button></Link>} /></Card>;

  const { customer, estimates, jobs, invoices, payments } = data;

  return (
    <>
      <div className="page-head">
        <div>
          <Link to="/customers" className="subtle" style={{ fontSize: 'var(--text-xs)' }}>← Customers</Link>
          <div className="page-title">{customer.displayName}</div>
          <div className="row gap-3 page-sub">
            <Badge tone={customer.type === 'commercial' ? 'info' : 'neutral'}>{customer.type}</Badge>
            {customer.phones?.[0] && <span>{customer.phones[0]}</span>}
            {customer.emails?.[0] && <span>{customer.emails[0]}</span>}
          </div>
        </div>
        <div className="row gap-2">
          <div className="stack" style={{ textAlign: 'right' }}><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>Outstanding</span><strong className="tabular" style={{ color: customer.outstandingBalanceCents ? 'var(--color-danger)' : 'inherit' }}>{money(customer.outstandingBalanceCents)}</strong></div>
          <Button onClick={() => setEstDrawer(true)}>+ New proposal</Button>
        </div>
      </div>

      {/* Pipeline stepper — the obvious lead → customer → proposal → job → invoice → paid path */}
      <PipelineStepper
        estimates={estimates} jobs={jobs} invoices={invoices}
        outstanding={customer.outstandingBalanceCents}
        onCreateProposal={() => setEstDrawer(true)}
        onSendProposal={(e) => act(`send${e.id}`, () => api.post(`/customers/${id}/estimates/${e.id}/send`), 'Proposal sent')}
        onApprove={(e) => setApproveEst(e)}
        onConvert={(e) => act(`conv${e.id}`, () => api.post(`/customers/${id}/estimates/${e.id}/convert`), 'Job created')}
        onInvoice={(j) => act(`inv${j.id}`, () => api.post(`/customers/${id}/jobs/${j.id}/invoice`), 'Invoice drafted')}
        onCollect={(inv) => setPayFor(inv)}
      />

      {/* Properties */}
      <Card style={{ marginBottom: 'var(--sp-5)' } as React.CSSProperties}>
        <div className="card-head"><span className="card-title">Properties <span className="subtle">· {properties.data?.items.length ?? 0}</span></span><Button size="sm" variant="secondary" onClick={() => setPropDrawer(true)}>+ Add property</Button></div>
        <div style={{ padding: '0 var(--sp-5)' }}>
          {(properties.data?.items.length ?? 0) === 0 ? <Muted>No service locations yet. Add the property where work happens.</Muted> : properties.data!.items.map((p) => (
            <div key={p.id} className="queue-item">
              <div className="stack">
                <strong>{p.label ?? p.address.line1}</strong>
                <span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{p.address.line1}, {p.address.city} {p.address.state} · {p.gateCode ? `gate ${p.gateCode}` : 'no gate code'}{p.accessInstructions ? ` · ${p.accessInstructions}` : ''}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Proposals (estimates) */}
      <Section title="Proposals" count={estimates.length}>
        {estimates.length === 0 ? <Muted>No proposals yet. Create one (Good/Better/Best) to start the job.</Muted> : estimates.map((e) => (
          <div key={e.id} className="queue-item">
            <div className="stack">
              <div className="row gap-2"><strong>{e.title ?? e.number}</strong> <Badge tone={statusTone(e.status)}>{label(e.status)}</Badge></div>
              <span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{e.number} · {money(e.totalCents)} · {e.options.length} option{e.options.length > 1 ? 's' : ''}</span>
            </div>
            <div className="row gap-2">
              {e.status === 'draft' && <Button size="sm" variant="secondary" loading={busy === `send${e.id}`} onClick={() => act(`send${e.id}`, () => api.post(`/customers/${id}/estimates/${e.id}/send`), 'Estimate sent')}>Send</Button>}
              {(e.status === 'sent' || e.status === 'viewed') && <Button size="sm" loading={busy === `appr${e.id}`} onClick={() => setApproveEst(e)}>Approve</Button>}
              {(e.status === 'approved') && <Button size="sm" loading={busy === `conv${e.id}`} onClick={() => act(`conv${e.id}`, () => api.post(`/customers/${id}/estimates/${e.id}/convert`), 'Job created from estimate')}>Convert to job →</Button>}
              {e.status === 'converted' && <span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>→ job created</span>}
            </div>
          </div>
        ))}
      </Section>

      {/* Jobs */}
      <Section title="Jobs" count={jobs.length}>
        {jobs.length === 0 ? <Muted>No jobs yet.</Muted> : jobs.map((j) => (
          <JobRow key={j.id} job={j} busy={busy}
            onStatus={(s) => act(`st${j.id}`, () => api.post(`/customers/${id}/jobs/${j.id}/status`, { status: s }), `Job → ${label(s)}`)}
            onInvoice={() => act(`inv${j.id}`, () => api.post(`/customers/${id}/jobs/${j.id}/invoice`), 'Invoice drafted from job')}
          />
        ))}
      </Section>

      {/* Invoices */}
      <Section title="Invoices" count={invoices.length} action={<Button size="sm" variant="secondary" onClick={() => setInvDrawer(true)}>+ New invoice</Button>}>
        {invoices.length === 0 ? <Muted>No invoices yet.</Muted> : invoices.map((i) => (
          <div key={i.id} className="queue-item">
            <div className="stack">
              <div className="row gap-2"><strong>{i.number}</strong> <Badge tone={statusTone(i.status)}>{label(i.status)}</Badge></div>
              <span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>Total {money(i.totalCents)} · Balance {money(i.balanceCents)} · due {i.dueDate}</span>
            </div>
            <div className="row gap-2">
              {i.status === 'draft' && <Button size="sm" variant="secondary" loading={busy === `snd${i.id}`} onClick={() => act(`snd${i.id}`, () => api.post(`/customers/${id}/invoices/${i.id}/send`), 'Invoice sent')}>Send</Button>}
              {i.balanceCents > 0 && i.status !== 'draft' && <Button size="sm" loading={busy === `card${i.id}`} onClick={() => chargeCard(i)}>Pay by card</Button>}
              {i.balanceCents > 0 && i.status !== 'draft' && <Button size="sm" variant="secondary" onClick={() => setPayFor(i)}>Record payment</Button>}
              {i.status === 'paid' && <span className="badge badge-success">Paid</span>}
            </div>
          </div>
        ))}
      </Section>

      {/* Payments */}
      {payments.length > 0 && (
        <Section title="Payments" count={payments.length}>
          {payments.map((p) => (
            <div key={p.id} className="queue-item">
              <div className="stack"><strong className="tabular">{money(p.amountCents)}</strong><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{label(p.method)} · {shortDate(p.createdAt)}</span></div>
              <Badge tone={statusTone(p.status)}>{label(p.status)}</Badge>
            </div>
          ))}
        </Section>
      )}

      {/* Equipment */}
      <Card style={{ marginBottom: 'var(--sp-5)' } as React.CSSProperties}>
        <div className="card-head"><span className="card-title">Equipment <span className="subtle">· {equipment.data?.items.length ?? 0}</span></span><Button size="sm" variant="secondary" onClick={() => setEquipDrawer(true)}>+ Add equipment</Button></div>
        <div style={{ padding: '0 var(--sp-5)' }}>
          {(equipment.data?.items.length ?? 0) === 0 ? <Muted>No equipment tracked. Add the units you service for full history.</Muted> : equipment.data!.items.map((e) => (
            <div key={e.id} className="queue-item">
              <div className="stack"><strong>{e.type}{e.manufacturer ? ` · ${e.manufacturer}` : ''}{e.model ? ` ${e.model}` : ''}</strong><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{e.serialNumber ? `SN ${e.serialNumber}` : 'no serial'}{e.installDate ? ` · installed ${shortDate(e.installDate)}` : ''}{e.warrantyExpires ? ` · warranty to ${shortDate(e.warrantyExpires)}` : ''}</span></div>
            </div>
          ))}
        </div>
      </Card>

      {/* Memberships */}
      <Card style={{ marginBottom: 'var(--sp-5)' } as React.CSSProperties}>
        <div className="card-head"><span className="card-title">Memberships <span className="subtle">· {memberships.data?.items.length ?? 0}</span></span><Button size="sm" variant="secondary" onClick={() => setMemberDrawer(true)}>+ Add plan</Button></div>
        <div style={{ padding: '0 var(--sp-5)' }}>
          {(memberships.data?.items.length ?? 0) === 0 ? <Muted>No service plan. Recurring plans auto-generate upcoming service jobs.</Muted> : memberships.data!.items.map((m) => (
            <div key={m.id} className="queue-item">
              <div className="stack"><div className="row gap-2"><strong>{m.name}</strong>{m.active && <Badge tone="success">Active</Badge>}</div><span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{money(m.priceCents)}/{m.billingInterval} · service every {m.serviceIntervalDays}d{m.nextServiceDate ? ` · next ${shortDate(m.nextServiceDate)}` : ''}</span></div>
            </div>
          ))}
        </div>
      </Card>

      {/* Communications */}
      <Card style={{ marginBottom: 'var(--sp-5)' } as React.CSSProperties}>
        <div className="card-head"><span className="card-title">Communications</span></div>
        <MessageThread customerId={id} />
      </Card>

      {estDrawer && <NewEstimateDrawer customerId={id} onClose={() => setEstDrawer(false)} onCreated={() => { refresh(); setEstDrawer(false); toast.push('Estimate created', 'success'); }} />}
      {payFor && <RecordPaymentDialog invoice={payFor} onClose={() => setPayFor(null)} onPaid={(amount, method) => act('pay', () => api.post(`/customers/${id}/payments`, { invoiceId: payFor.id, method, amountCents: amount }), 'Payment recorded').then(() => setPayFor(null))} />}
      {approveEst && <ApproveDialog estimate={approveEst} onClose={() => setApproveEst(null)} onApprove={(optId) => act(`appr${approveEst.id}`, () => api.post(`/customers/${id}/estimates/${approveEst.id}/approve`, { selectedOptionId: optId }), 'Estimate approved').then(() => setApproveEst(null))} />}
      {propDrawer && <AddPropertyDrawer customerId={id} onClose={() => setPropDrawer(false)} onCreated={() => { setPropDrawer(false); toast.push('Property added', 'success'); }} />}
      {invDrawer && <NewInvoiceDrawer customerId={id} onClose={() => setInvDrawer(false)} onCreated={() => { refresh(); setInvDrawer(false); toast.push('Invoice created', 'success'); }} />}
      {equipDrawer && <AddEquipmentDrawer customerId={id} onClose={() => setEquipDrawer(false)} onDone={() => { qc.invalidateQueries({ queryKey: ['equipment', id] }); setEquipDrawer(false); toast.push('Equipment added', 'success'); }} />}
      {memberDrawer && <AddMembershipDrawer customerId={id} onClose={() => setMemberDrawer(false)} onDone={() => { qc.invalidateQueries({ queryKey: ['memberships', id] }); setMemberDrawer(false); toast.push('Plan added', 'success'); }} />}
    </>
  );
}

function AddEquipmentDrawer({ customerId, onClose, onDone }: { customerId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [f, setF] = useState({ type: '', manufacturer: '', model: '', serialNumber: '', installDate: '' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await api.post(`/customers/${customerId}/equipment`, { ...f, installDate: f.installDate || undefined }); onDone(); }
    catch (err) { toast.push(err instanceof ApiRequestError ? err.message : 'Failed', 'error'); }
    finally { setSaving(false); }
  };
  return (
    <Drawer title="Add equipment" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={saving} onClick={submit} disabled={!f.type}>Add</Button></>}>
      <div className="stack gap-4">
        <Field label="Type"><Input value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} placeholder="e.g. AC Condenser, Water Heater" autoFocus /></Field>
        <div className="row gap-3"><Field label="Manufacturer"><Input value={f.manufacturer} onChange={(e) => setF({ ...f, manufacturer: e.target.value })} /></Field><Field label="Model"><Input value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} /></Field></div>
        <Field label="Serial number"><Input value={f.serialNumber} onChange={(e) => setF({ ...f, serialNumber: e.target.value })} /></Field>
        <Field label="Install date"><Input type="date" value={f.installDate} onChange={(e) => setF({ ...f, installDate: e.target.value })} /></Field>
      </div>
    </Drawer>
  );
}

function NewInvoiceDrawer({ customerId, onClose, onCreated }: { customerId: string; onClose: () => void; onCreated: () => void }) {
  const toast = useToast();
  const [lines, setLines] = useState<{ name: string; qty: string; price: string }[]>([{ name: '', qty: '1', price: '' }]);
  const [taxRate, setTaxRate] = useState('0');
  const [saving, setSaving] = useState(false);
  const total = lines.reduce((s, l) => s + (parseFloat(l.price || '0') || 0) * (parseFloat(l.qty || '1') || 1), 0);

  const submit = async () => {
    const lineItems = lines.filter((l) => l.name.trim()).map((l) => ({
      kind: 'service' as const, name: l.name, quantity: parseFloat(l.qty || '1') || 1,
      unitPrice: Math.round((parseFloat(l.price || '0') || 0) * 100), taxable: true,
    }));
    if (lineItems.length === 0) { toast.push('Add at least one line item', 'error'); return; }
    setSaving(true);
    try {
      await api.post(`/customers/${customerId}/invoices`, { customerId, taxRate: parseFloat(taxRate || '0') || 0, lineItems });
      onCreated();
    } catch (err) { toast.push(err instanceof ApiRequestError ? err.message : 'Could not create invoice', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <Drawer title="New invoice" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={saving} onClick={submit}>Create invoice ({money(Math.round(total * 100))})</Button></>}>
      <div className="stack gap-3">
        {lines.map((l, i) => (
          <div key={i} className="row gap-2">
            <Input placeholder="Description" value={l.name} onChange={(e) => setLines((s) => s.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className="grow" />
            <Input placeholder="Qty" value={l.qty} onChange={(e) => setLines((s) => s.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} style={{ width: 60 }} inputMode="decimal" />
            <Input placeholder="Price" value={l.price} onChange={(e) => setLines((s) => s.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} style={{ width: 90 }} inputMode="decimal" />
            {lines.length > 1 && <Button variant="ghost" size="sm" onClick={() => setLines((s) => s.filter((_, j) => j !== i))}>✕</Button>}
          </div>
        ))}
        <Button variant="ghost" size="sm" onClick={() => setLines((s) => [...s, { name: '', qty: '1', price: '' }])}>+ Add line</Button>
        <Field label="Tax rate (%)"><Input value={taxRate} onChange={(e) => setTaxRate(e.target.value)} style={{ width: 100 }} inputMode="decimal" /></Field>
      </div>
    </Drawer>
  );
}

function AddMembershipDrawer({ customerId, onClose, onDone }: { customerId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [f, setF] = useState({ name: 'Comfort Club', price: '199', billingInterval: 'annual', serviceIntervalDays: '180', startDate: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/customers/${customerId}/memberships`, {
        name: f.name, priceCents: Math.round(parseFloat(f.price || '0') * 100), billingInterval: f.billingInterval,
        serviceIntervalDays: parseInt(f.serviceIntervalDays || '180', 10), startDate: f.startDate,
      });
      onDone();
    } catch (err) { toast.push(err instanceof ApiRequestError ? err.message : 'Failed', 'error'); }
    finally { setSaving(false); }
  };
  return (
    <Drawer title="Add service plan" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={saving} onClick={submit} disabled={!f.name}>Add plan</Button></>}>
      <div className="stack gap-4">
        <Field label="Plan name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
        <div className="row gap-3">
          <Field label="Price ($)"><Input value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} style={{ width: 100 }} inputMode="decimal" /></Field>
          <Field label="Billing"><select className="select" value={f.billingInterval} onChange={(e) => setF({ ...f, billingInterval: e.target.value })}><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="annual">Annual</option></select></Field>
        </div>
        <Field label="Service every (days)"><Input value={f.serviceIntervalDays} onChange={(e) => setF({ ...f, serviceIntervalDays: e.target.value })} style={{ width: 100 }} inputMode="numeric" /></Field>
        <Field label="Start date"><Input type="date" value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value })} /></Field>
      </div>
    </Drawer>
  );
}

function AddPropertyDrawer({ customerId, onClose, onCreated }: { customerId: string; onClose: () => void; onCreated: () => void }) {
  const create = useCreateProperty(customerId);
  const toast = useToast();
  const [f, setF] = useState({ label: '', line1: '', city: '', state: '', postalCode: '', gateCode: '', accessInstructions: '' });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const submit = async () => {
    setErrs({});
    try {
      await create.mutateAsync({
        label: f.label || undefined,
        address: { line1: f.line1, city: f.city, state: f.state, postalCode: f.postalCode, country: 'US' },
        gateCode: f.gateCode || undefined,
        accessInstructions: f.accessInstructions || undefined,
      });
      onCreated();
    } catch (err) {
      if (err instanceof ApiRequestError) { setErrs(err.fields ?? {}); toast.push(err.message, 'error'); }
    }
  };
  return (
    <Drawer title="Add property" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={create.isPending} onClick={submit}>Add property</Button></>}>
      <div className="stack gap-4">
        <Field label="Label (optional)"><Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="e.g. Main home, Building A" autoFocus /></Field>
        <Field label="Street address" error={errs['address.line1']}><Input value={f.line1} onChange={(e) => setF({ ...f, line1: e.target.value })} /></Field>
        <div className="row gap-3">
          <Field label="City" error={errs['address.city']}><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></Field>
          <Field label="State" error={errs['address.state']}><Input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} style={{ width: 80 }} /></Field>
          <Field label="ZIP" error={errs['address.postalCode']}><Input value={f.postalCode} onChange={(e) => setF({ ...f, postalCode: e.target.value })} style={{ width: 100 }} /></Field>
        </div>
        <Field label="Gate code (optional)"><Input value={f.gateCode} onChange={(e) => setF({ ...f, gateCode: e.target.value })} style={{ maxWidth: 160 }} /></Field>
        <Field label="Access instructions (optional)"><Textarea value={f.accessInstructions} onChange={(e) => setF({ ...f, accessInstructions: e.target.value })} placeholder="Gate on left, dog in yard, key at front office…" /></Field>
      </div>
    </Drawer>
  );
}

function PipelineStepper({ estimates, jobs, invoices, outstanding, onCreateProposal, onSendProposal, onApprove, onConvert, onInvoice, onCollect }: {
  estimates: Estimate[]; jobs: Job[]; invoices: Invoice[]; outstanding: number;
  onCreateProposal: () => void; onSendProposal: (e: Estimate) => void; onApprove: (e: Estimate) => void;
  onConvert: (e: Estimate) => void; onInvoice: (j: Job) => void; onCollect: (inv: Invoice) => void;
}) {
  const draft = estimates.find((e) => e.status === 'draft');
  const sent = estimates.find((e) => e.status === 'sent' || e.status === 'viewed');
  const approvedUnconverted = estimates.find((e) => e.status === 'approved');
  const hasProposal = estimates.length > 0;
  const proposalDone = estimates.some((e) => ['approved', 'converted'].includes(e.status));
  const hasJob = jobs.length > 0;
  const completedNoInvoice = jobs.find((j) => j.status === 'completed' && !j.invoiceId);
  const hasInvoice = invoices.length > 0;
  const unpaid = invoices.find((i) => i.balanceCents > 0 && i.status !== 'draft');
  const paidDone = hasInvoice && invoices.some((i) => i.status === 'paid') && outstanding === 0;

  const steps: { label: string; done: boolean; state: string; cta?: { label: string; onClick: () => void } }[] = [
    { label: 'Customer', done: true, state: 'Created' },
    {
      label: 'Proposal', done: proposalDone, state: !hasProposal ? 'Not started' : proposalDone ? 'Approved' : sent ? 'Awaiting approval' : 'Draft',
      cta: !hasProposal ? { label: 'Create proposal', onClick: onCreateProposal }
        : draft ? { label: 'Send proposal', onClick: () => onSendProposal(draft) }
        : sent ? { label: 'Mark approved', onClick: () => onApprove(sent) } : undefined,
    },
    {
      label: 'Job', done: hasJob, state: hasJob ? 'Created' : 'Not started',
      cta: !hasJob && approvedUnconverted ? { label: 'Convert to job', onClick: () => onConvert(approvedUnconverted) } : undefined,
    },
    {
      label: 'Invoice', done: hasInvoice, state: hasInvoice ? 'Created' : 'Not started',
      cta: !hasInvoice && completedNoInvoice ? { label: 'Create invoice', onClick: () => onInvoice(completedNoInvoice) } : undefined,
    },
    {
      label: 'Paid', done: paidDone, state: paidDone ? 'Paid' : unpaid ? money(unpaid.balanceCents) + ' due' : 'Not started',
      cta: unpaid ? { label: 'Collect payment', onClick: () => onCollect(unpaid) } : undefined,
    },
  ];
  const currentIdx = steps.findIndex((s) => !s.done);

  return (
    <div className="pipeline">
      {steps.map((s, i) => (
        <div key={s.label} className={`pstep ${s.done ? 'done' : i === currentIdx ? 'current' : ''}`}>
          <div className="pstep-label"><span className="pstep-num">{s.done ? '✓' : i + 1}</span>{s.label}</div>
          <div className="pstep-state">{s.state}</div>
          {i === currentIdx && s.cta && <div className="pstep-cta"><Button size="sm" onClick={s.cta.onClick}>{s.cta.label} →</Button></div>}
        </div>
      ))}
    </div>
  );
}

function Section({ title, count, action, children }: { title: string; count: number; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Card style={{ marginBottom: 'var(--sp-5)' } as React.CSSProperties} className="">
      <div className="card-head"><span className="card-title">{title} <span className="subtle">· {count}</span></span>{action}</div>
      <div style={{ padding: '0 var(--sp-5)' }}>{children}</div>
    </Card>
  );
}
function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: 'var(--sp-5) 0' }}><span className="muted" style={{ fontSize: 'var(--text-sm)' }}>{children}</span></div>;
}

function JobRow({ job, busy, onStatus, onInvoice }: { job: Job; busy: string | null; onStatus: (s: JobStatus) => void; onInvoice: () => void }) {
  const nexts = JOB_TRANSITIONS[job.status].filter((s) => s !== 'canceled');
  return (
    <div className="queue-item">
      <div className="stack">
        <div className="row gap-2"><strong>{job.title}</strong> <Badge tone={statusTone(job.status)}>{label(job.status)}</Badge></div>
        <span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{job.number}{job.assignedUserIds.length ? ` · ${job.assignedUserIds.length} assigned` : ''}</span>
      </div>
      <div className="row gap-2 wrap right">
        {nexts.map((s) => <Button key={s} size="sm" variant="secondary" loading={busy === `st${job.id}`} onClick={() => onStatus(s)}>{label(s)}</Button>)}
        {job.status === 'completed' && !job.invoiceId && <Button size="sm" loading={busy === `inv${job.id}`} onClick={onInvoice}>Create invoice</Button>}
      </div>
    </div>
  );
}

function RecordPaymentDialog({ invoice, onClose, onPaid }: { invoice: Invoice; onClose: () => void; onPaid: (amountCents: number, method: string) => void }) {
  const [amount, setAmount] = useState((invoice.balanceCents / 100).toFixed(2));
  const [method, setMethod] = useState('card');
  return (
    <Dialog title={`Record payment · ${invoice.number}`} onClose={onClose}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => onPaid(Math.round(parseFloat(amount || '0') * 100), method)}>Record {money(Math.round(parseFloat(amount || '0') * 100))}</Button></>}>
      <div className="stack gap-4">
        <div className="banner banner-info">Balance due: <strong>&nbsp;{money(invoice.balanceCents)}</strong></div>
        <label className="field"><span className="field-label">Amount</span><input className="input" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" /></label>
        <label className="field"><span className="field-label">Method</span>
          <select className="select" value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="card">Card</option><option value="ach">ACH / bank</option><option value="cash">Cash</option><option value="check">Check</option>
          </select>
        </label>
      </div>
    </Dialog>
  );
}

function ApproveDialog({ estimate, onClose, onApprove }: { estimate: Estimate; onClose: () => void; onApprove: (optionId: string) => void }) {
  const [selected, setSelected] = useState(estimate.options.find((o) => o.recommended)?.id ?? estimate.options[0]?.id ?? '');
  return (
    <Dialog title="Approve estimate" onClose={onClose}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => onApprove(selected)} disabled={!selected}>Approve selected option</Button></>}>
      <div className="stack gap-3">
        <span className="muted" style={{ fontSize: 'var(--text-sm)' }}>Choose the option the customer approved:</span>
        {estimate.options.map((o) => {
          const total = o.lineItems.reduce((s, li) => s + Math.round(li.unitPrice * li.quantity), 0);
          return (
            <label key={o.id} className="card card-pad row between" style={{ cursor: 'pointer', borderColor: selected === o.id ? 'var(--color-primary)' : undefined }}>
              <div className="row gap-2"><input type="radio" name="opt" checked={selected === o.id} onChange={() => setSelected(o.id)} /><strong>{o.name}</strong>{o.recommended && <Badge tone="info">Recommended</Badge>}</div>
              <strong className="tabular">{money(total)}</strong>
            </label>
          );
        })}
      </div>
    </Dialog>
  );
}
